import React, { Fragment, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { flow, get, groupBy, map } from 'lodash-es';
import classnames from 'classnames';
import Container from 'reactstrap/lib/Container';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import Button from 'reactstrap/lib/Button';
import { connect } from 'react-redux';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { venomHistory } from 'client/utils/history/venom-history';
import {
  getDisplayedStyleId,
  getTcoPriceStrings,
  isTcoDataAvailable,
} from 'client/site-modules/cost-to-own/utils/tco-utils';
import { TrackingHandler } from 'client/tracking/handler';
import { TrackingMap } from 'client/tracking/maps/cost-to-own/cost-to-own';
import { buildPopularStylesPath, buildVehiclePath, VehicleEntities, VehicleModel } from 'client/data/models/vehicle-v2';
import { buildTcoPath, CostToOwnModel, TcoEntities } from 'client/data/models/cost-to-own';
import { VisitorEntities, VisitorModel } from 'client/data/models/visitor';
import { getUrlPathFromParams } from 'site-modules/shared/utils/core-breadcrumbs-builder';
import { buildSrpLink } from 'site-modules/shared/utils/srp-link-constructor';
import { buildDisplayVehicle } from 'site-modules/shared/utils/core-page/display-name-builders';
import { isUsed as isUsedPubState } from 'site-modules/shared/utils/publication-states';
import { getTCOUrl } from 'site-modules/shared/utils/core-link-constructor';
import { MoreAboutMMYLink } from 'site-modules/shared/components/more-about-mmy-link/more-about-mmy-link';
import { CoreBreadcrumbs } from 'site-modules/shared/components/core-breadcrumbs/core-breadcrumbs';
import { VehiclePhoto } from 'site-modules/shared/components/vehicle-photo/vehicle-photo';
import { PhotoflipperButtonAsync } from 'site-modules/shared/components/photoflipper-core/photoflipper-button/photoflipper-button-async';
import { BuildPriceAd } from 'site-modules/shared/components/native-ad/buildprice-ad/buildprice-ad';
import { ArrowLink } from 'site-modules/shared/components/arrow-link/arrow-link';
import { PageLinksWidget } from 'site-modules/shared/components/page-links-widget/page-links-widget';
import { PricingCarouselFooter } from 'site-modules/shared/components/core-page/pricing-carousel/pricing-carousel-footer';
import { AspectRatioContainer } from 'site-modules/shared/components/aspect-ratio-container/aspect-ratio-container';
import { SelectionSection } from 'site-modules/cost-to-own/components/selection-section/selection-section';
import { PricingSection } from 'site-modules/cost-to-own/components/pricing-section/pricing-section';
import { CostsChart } from 'site-modules/cost-to-own/components/costs-chart/costs-chart';
import { CostsTable } from 'site-modules/cost-to-own/components/costs-table/costs-table';
import { TcoInfoSection } from 'site-modules/cost-to-own/components/tco-info-section/tco-info-section';
import { EvTaxCreditMessage } from 'site-modules/cost-to-own/components/ev-tax-credit-message/ev-tax-credit-message';

import './cost-to-own.scss';

export const PAGE_DISPLAY_NAME = 'Cost to Own';
export const URL_PATH = 'cost-to-own';

function CostToOwnUI({
  vehicle = null,
  styles = null,
  params,
  tcoStylesData = null,
  visitorLocation,
  location = {},
  isMobile = false,
}) {
  const selectedStyleId = getDisplayedStyleId({ location, styles, tcoStylesData });
  const isPricesTracked = useRef(false);

  useEffect(() => {
    TrackingHandler.useMap(TrackingMap);
  }, []);

  useEffect(() => {
    if (!(tcoStylesData && styles) || isPricesTracked.current) {
      return;
    }

    isPricesTracked.current = true;

    const { totalTcoPrice, totalCashPrice } = getTcoPriceStrings({ tcoStylesData, styleId: get(styles, '[0].id') });

    TrackingHandler.fireEvent('display_tco', { event_data: { value: totalTcoPrice } });
    TrackingHandler.fireEvent('display_total_cash', { event_data: { value: totalCashPrice } });
  });

  const changeStyle = useCallback(style => {
    const styleId = get(style, 'id');
    const { modelName, name, makeSlug, modelSlug, year } = style;

    TrackingHandler.fireEvent('select_style', {
      event_data: {
        value: `${modelName} ${name}`,
      },
    });

    venomHistory.push(getTCOUrl({ makeSlug, modelSlug, year }, styleId));
  }, []);

  if (!vehicle || !selectedStyleId) {
    return (
      <main id="main-content" tabIndex={-1} className="cost-to-own container">
        Loading&hellip;
      </main>
    );
  }

  const groupedStyles = groupBy(styles, 'submodels.name');
  const selectedStyle = styles.find(style => style.id === selectedStyleId) || {};
  const isCurrentDataAvailable = isTcoDataAvailable({ tcoStylesData, styleId: selectedStyleId });

  const displayVehicle = buildDisplayVehicle({ makeModelSubmodelYear: vehicle, params });

  const {
    make: { name: makeName, slug: makeSlug },
    model: { name: modelName, slug: modelSlug },
    year,
    pubStates,
  } = vehicle;

  const isUsed = isUsedPubState(pubStates);

  const extendedMMY = {
    ...vehicle,
    submodels: selectedStyle.submodels || vehicle.submodels,
  };

  return (
    <Fragment>
      <Container tag="main" id="main-content" tabIndex={-1} className="cost-to-own" data-tracking-parent="cost-to-own">
        <CoreBreadcrumbs
          className="ps-0 mb-2"
          vehicle={vehicle}
          breadcrumbsParams={{
            params,
            pageDisplayName: PAGE_DISPLAY_NAME,
            urlPath: getUrlPathFromParams(params, URL_PATH),
          }}
        />
        <h1 className="heading-3 mb-0_5">
          {displayVehicle} {PAGE_DISPLAY_NAME}
        </h1>
        <MoreAboutMMYLink
          vehicle={vehicle}
          className="d-block mb-1_5"
          dataTrackingId="click_go_to_core"
          dataTrackingValue="more_about"
          linkToReview={isUsed}
        />
        <Row className="mb-md-1_5">
          <Col xs={12} md={6} lg={5}>
            <AspectRatioContainer aspectRatio="3:2">
              <VehiclePhoto params={params} vehicle={extendedMMY} />
            </AspectRatioContainer>
            <PhotoflipperButtonAsync
              makeSlug={params.make}
              modelSlug={params.model}
              year={year}
              submodel={get(extendedMMY, 'submodels.name', null)}
            >
              <Button
                color="link"
                className="photos-btn p-0 size-16 text-primary-darker border-0 mb-2 mb-md-0"
                data-tracking-id="click_view_photos"
              >
                Photos &&nbsp;Videos
              </Button>
            </PhotoflipperButtonAsync>
          </Col>
          <Col xs={12} md={6} lg={5}>
            <h2 className="heading-4 mb-1 mb-md-1_5">Total 5-Year Ownership Costs</h2>
            <SelectionSection
              styles={styles}
              tcoStylesData={tcoStylesData}
              selectedStyleId={selectedStyleId}
              onStyleSelect={changeStyle}
            />
            {map(groupedStyles, (groupStyles, submodel) => (
              <Fragment key={submodel}>
                <h3 className="d-none">{submodel}</h3>
                {groupStyles.map(({ id, name }) => {
                  const { totalTcoPrice, totalCashPrice } = getTcoPriceStrings({ tcoStylesData, styleId: id });

                  return (
                    <Fragment key={id}>
                      <h4 className="d-none">{name}</h4>
                      <PricingSection
                        tcoString={totalTcoPrice}
                        cashPriceString={totalCashPrice}
                        className={classnames('mb-1_5 mb-md-0', { 'd-none': id !== selectedStyleId })}
                      />
                    </Fragment>
                  );
                })}
              </Fragment>
            ))}
            <Row>
              <Col xs={12} xl={9}>
                <EvTaxCreditMessage
                  className="mt-md-1 mb-2 mb-md-0 px-1_5"
                  styleId={selectedStyleId}
                  vehicleParams={{ makeName, modelName, year, makeSlug, modelSlug }}
                  text="This car may qualify for a Federal EV Tax Credit."
                  isUsed={isUsed}
                />
              </Col>
            </Row>
          </Col>
        </Row>
        <Row>
          <Col xs={12} lg={9}>
            {isCurrentDataAvailable && (
              <Fragment>
                <CostsChart data={tcoStylesData[selectedStyleId].total} isMobile={isMobile} className="mb-2" />
                <h2 className="heading-4 p-0 mb-0_5">Ownership Costs: 5-Year Breakdown</h2>
              </Fragment>
            )}
            {map(groupedStyles, (groupStyles, submodel) =>
              groupStyles.map(style => {
                const tco = get(tcoStylesData, `[${style.id}]`, {});

                const fullStyleName = `${style.year} ${submodel} ${style.name}`;
                const isSelectedStyle = style.id === selectedStyleId;

                const isDataAvailable = isTcoDataAvailable({ tcoStylesData, styleId: style.id });

                return isDataAvailable ? (
                  <section key={style.id} className={classnames({ 'd-none': !isSelectedStyle })}>
                    <div className="mb-1">
                      <span className="size-16">Selected Model: </span>
                      <h3 className="d-inline size-16 fw-normal">{fullStyleName}</h3>
                    </div>
                    <CostsTable data={tco} />
                  </section>
                ) : (
                  isSelectedStyle && <p key={style.id}>Data for {fullStyleName} is not available.</p>
                );
              })
            )}
            <EvTaxCreditMessage
              className="mb-1 px-0_75"
              styleId={selectedStyleId}
              vehicleParams={{ makeName, modelName, year, makeSlug, modelSlug }}
              text="Ownership costs do not include any Federal EV Tax Credits."
              isUsed={isUsed}
            />
            <p className="label text-gray-darker mb-1_5">
              *Based on a 5-year estimate with 15,000 miles driven per year.
            </p>
            <Button
              outline
              color="primary-b"
              className="shop-btn size-16 mb-1_5"
              tag="a"
              href={buildSrpLink({ makeModelSubmodelYear: vehicle, params, visitorLocation })}
              rel="nofollow"
              data-tracking-id="click_shop_btn"
            >
              Shop now
            </Button>
            <ArrowLink
              to={`/${params.make}/${params.model}/${params.year}/`}
              data-tracking-id="click_learn_about"
              data-tracking-value="learn_about"
            >
              Learn about the {year} {modelName}
            </ArrowLink>
            <hr className="mt-1_5 mb-2" />
            <TcoInfoSection />
            <hr className="mt-1_5 mb-2" />
            <BuildPriceAd mobile={isMobile} wrapperClass="build-price-tco" includeMrectAdCall={!isUsed} />
          </Col>
        </Row>
        {!isUsed && <PricingCarouselFooter params={params} srpParams={{ ...params, inventorytype: 'new' }} />}
        <hr className="d-none d-md-block mt-1_5 mb-2" />
        <PageLinksWidget divideSections={false} title={`Related ${displayVehicle} info`} />
      </Container>
    </Fragment>
  );
}

CostToOwnUI.propTypes = {
  location: PropTypes.shape({}),
  styles: VehicleEntities.Styles,
  tcoStylesData: PropTypes.objectOf(TcoEntities.StyleTco),
  params: PropTypes.shape({
    make: PropTypes.string,
    model: PropTypes.string,
    year: PropTypes.string,
  }).isRequired,
  visitorLocation: VisitorEntities.Location.isRequired,
  isMobile: PropTypes.bool,
  vehicle: VehicleEntities.MakeModelSubmodelYear,
};

const mapStateToProps = state => ({
  isMobile: state.mobile,
  location: get(state, 'pageContext.location'),
});

export const propsAreEqual = (prevProps, { tcoStylesData }) => !tcoStylesData;
export const CostToOwn = flow(
  component =>
    connectToModel(component, {
      styles: bindToPath(({ vehicle, params }) => !!vehicle && buildPopularStylesPath(params), VehicleModel),
      tcoStylesData: bindToPath(({ vehicle, params }) => !!vehicle && buildTcoPath(params), CostToOwnModel),
      visitorLocation: bindToPath('location', VisitorModel),
    }),
  component =>
    connectToModel(component, {
      vehicle: bindToPath(({ params }) => buildVehiclePath(params), VehicleModel),
    })
)(connect(mapStateToProps)(React.memo(CostToOwnUI, propsAreEqual)));
